/* eslint-disable max-lines */
import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  eq,
  find,
  forEach,
  get,
  groupBy,
  gt,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNil,
  keyBy,
  map,
  reduce,
  set,
  size,
  some,
  sortBy,
  toLower,
  toPairs,
  values,
} from 'lodash';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { BaseEditorContainerComponent } from '@app/containers/base';
import {
  CustomRedirect,
  ENTITY_TYPES,
  IBaseEntityResponse,
  IBaseEntityType,
  IFormRules,
  IGridFlatInventoryItemVenue,
  IGridFlatInventoryItemVenueTotal,
  IInventoryItemVenue,
  ILimit,
  IMeasure,
  IModalDialogOptions,
  IOrderFormFieldPath,
  IOrderInventory,
  IVendorMeasure,
  IXVIListColumn,
  MEASURE_TRANSACTION,
  NegativeNumberType,
  NotificationType,
} from '@shared/types';

import { updateTextWithSelectedValue } from '@app/shared/utils/entity-selection-utils';
import { getSubtractDateString, toDate } from '@app/shared/utils/date-utils';
import { SelectOption } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/common.type';
import { lazyInject } from '@containers/base/decorators';
import {
  CompanyUtilService,
  NumberFormatService,
  OrderItemFormService,
  OrdersService,
  OrderTotalFormService,
  ValidationService,
} from '@app/shared/services';
import { APIActions, UiActions } from '@store/actions';
import { BTN_CANCEL, BTN_YES } from '@app/text-content/button-templates';
import { observableCombineLatest, rx } from '@shared/rx';
import {
  BTN_PRIMARY_CLASS,
  ENTITY_ID_PATH,
  KEY_CODES,
  MAX_URL_QUERY_NUMBER,
  ONLY_DATE_SUBSTRING_LENGTH,
} from '@config/global-constants';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import { ORDER_STATUS_VALUE } from '@app/shared/types/enums/order-status.enum';
import { InventoryItemVenueService } from '@app/shared/services/inventory-item-venue';
import { MODAL_TEMPLATES } from '@app/text-content';
import {
  ORDER_OPERATION_STATUS_MAPPING,
  TOASTER_ERROR_MESSAGE,
  TOASTER_SUBMITTED_SUCCESSFULLY_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
  TOASTER_TRANSACTION_FAILED_MESSAGE,
} from '@shared/constants';
import {
  ITreeConverterConfig,
  ITreeData,
} from '@shared/types/common/tree.type';
import {
  generateId,
  generateUnitTextV2,
  getDefaultMeasure,
  getFormBtnHidden,
  judgeScanned,
} from '@shared/utils';
import { flatTreeDataByHasChildren, generateObjectId } from '@app/shared/utils';
import { getSingularEntityName } from '@app/shared/configs';
import { COMMON_FORM_PATH } from '@app/shared/constants/form-paths';
import { getMeasureFromVendor } from '@shared/utils/inventory-item.utils';
import { DecimalNumber } from '@app/shared/common';
import { hiddenVendorStatus } from '@app/shared/services/orders/orders.definition';
import { ORDER_FORM_FIELD_PATHS } from '@app/shared/services/orders/order-form-field-paths';
import { OrderPdfService } from '@app/shared/services/orders/order.pdf.service';
import { appendInitialFactorValueAndCount } from '@shared/services/orders/order.util';
import { addPendingOrderModalOptions } from '@containers/orders/containers/edit/modal.definition';
import { I18n } from '@app/i18n/strings-keys';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-order-edit-container',
  templateUrl: '../../../base/editor/xux-edit.base.container.html',
  styleUrls: [
    '../../../base/editor/xux-edit.base.container.scss',
    './edit.orders.form.scss',
  ],
  encapsulation: ViewEncapsulation.None,
})
export class OrdersEditContainerComponent
  extends BaseEditorContainerComponent
  implements OnInit, OnDestroy
{
  @lazyInject(OrderItemFormService)
  public entityFormService: OrderItemFormService;
  @lazyInject(CompanyUtilService)
  public companyUtilsService: CompanyUtilService;
  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(OrderTotalFormService)
  public totalFromService: OrderTotalFormService;
  @lazyInject(OrdersService) public ordersService: OrdersService;
  @lazyInject(OrderPdfService) public orderPdfService: OrderPdfService;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(APIActions) public apiActions: APIActions;
  @lazyInject(UiActions) public uiActions: UiActions;

  public entityIdPath: string = 'transaction_id';
  public title: string = '';
  public xuxTheme: boolean = true;
  public summaryDisabled: boolean = true;
  public currentVendorId: string;
  public currentSiteId: string;
  public canExportPDF: boolean = true;
  public canPrint: boolean = true;
  public showExportButton: boolean = true;
  public inventoryItemVenueMap: IBaseEntityType = {};

  public formFieldPath: IOrderFormFieldPath = ORDER_FORM_FIELD_PATHS;

  public finalStatus: ORDER_STATUS_VALUE[] = [
    ORDER_STATUS_VALUE.Canceled,
    ORDER_STATUS_VALUE.Completed,
    ORDER_STATUS_VALUE.Rejected,
  ];

  public orderDateLimit: ILimit<string> = {
    minimumObs: new BehaviorSubject(null),
    maximumObs: new BehaviorSubject(null),
  };

  public deliveryDateLimit: ILimit<string> = {
    minimumObs: new BehaviorSubject(null),
    maximumObs: new BehaviorSubject(null),
  };

  public removeInfoTitle$: Subject<string> = new Subject();
  public modifiedInfoTitle$: Subject<string> = new Subject();

  public transactionStatusOptions: SelectOption[] = [
    { text: I18n.Status.Open, value: ORDER_STATUS_VALUE.Open },
    { text: I18n.Status.Pending, value: ORDER_STATUS_VALUE.Pending },
    { text: I18n.Status.New, value: ORDER_STATUS_VALUE.New },
    { text: I18n.Status.Canceled, value: ORDER_STATUS_VALUE.Canceled },
    { text: I18n.Status.Completed, value: ORDER_STATUS_VALUE.Completed },
    { text: I18n.Status.Rejected, value: ORDER_STATUS_VALUE.Rejected },
  ];
  public defaultItemListColumn$: Subject<IXVIListColumn[]> = new Subject();
  public hiddenRemoveInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public hiddenModifiedInfo$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public hiddenVendor$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public hiddenSummary$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public datePickerDisabled$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  public isOpenStatus$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isAcceptForOrder$: BehaviorSubject<boolean> = new BehaviorSubject(
    true,
  );
  public isAddAdditionalTotalFormColumns$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public isInventoryItemVenuesGridBusy$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);

  public get vendorEntityIdValueChange$(): Observable<string> {
    return this.getFieldChangeFilterEmpty(this.formFieldPath.vendorEntityId);
  }

  public get siteIdValueChange$(): Observable<string> {
    return this.getFieldChangeFilterEmpty(this.formFieldPath.siteId);
  }

  public get taxFeeValueChange$(): Observable<string> {
    return this.fieldValueChanges(this.formFieldPath.taxFee);
  }

  public get deliveryFeeValueChange$(): Observable<string> {
    return this.fieldValueChanges(this.formFieldPath.deliveryFee);
  }

  public get statusValueChange$(): Observable<string> {
    return this.getFieldChangeFilterEmpty(this.formFieldPath.status);
  }

  /**
   * get order item list obs
   */
  public get orderItemListColumnObs(): Observable<IXVIListColumn[]> {
    return this.defaultItemListColumn$.pipe(rx.distinctUntilChanged());
  }

  public formRules: IFormRules;

  protected createWithDataCarryFields: string[] = [
    'vendor_entity_id',
    'vendor_name',
    'delivery_date',
    'site_id',
    'site_name',
    'transaction_date',
  ];

  protected getSidebarOperationStatusMapping(): Record<string, string> {
    return ORDER_OPERATION_STATUS_MAPPING;
  }

  protected getEntityRequiredFieldsForUpdate(): string[] {
    return [
      '_id',
      'transaction_id',
      'type',
      'external_order_number',
      'invoice_number',
      this.formFieldPath.status,
      this.formFieldPath.siteId,
      this.formFieldPath.inventoryItemVenues,
      this.formFieldPath.orderDate,
      this.formFieldPath.deliveryDate,
      this.formFieldPath.vendorEntityId,
      this.formFieldPath.vendorName,
    ];
  }

  public async acceptButtonClickHandler(
    documentItem: IOrderInventory = this.document,
  ): Promise<void> {
    const modalOptions: IModalDialogOptions = {
      title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
      text: this.i18nService.instant(I18n.ConfirmMessages.AcceptEntity, {
        entityType: this.i18nService.instant(
          getSingularEntityName(ENTITY_TYPES.ORDER),
        ),
      }),
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) return;
    setFormFieldValue(
      this.form,
      this.formFieldPath.status,
      ORDER_STATUS_VALUE.Completed,
    );
    const saveDocument = await this.submitBtnClick(documentItem);
    if (saveDocument) {
      this.apiActions.openDocumentEditor(this.entityType, documentItem._id, {
        forceReload: true,
      });
    } else {
      setFormFieldValue(
        this.form,
        this.formFieldPath.status,
        get(this.document, this.formFieldPath.status, ''),
        false,
      );
    }
  }

  public async rejectAllButtonClickHandler(
    documentItem: IOrderInventory = this.document,
  ): Promise<void> {
    const modalOptions: IModalDialogOptions = {
      title: I18n.Common.Confirm,
      text: I18n.ConfirmMessages.RejectEntireOrder,
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) return;
    setFormFieldValue(
      this.form,
      this.formFieldPath.status,
      ORDER_STATUS_VALUE.Rejected,
    );
    const rejectDocument = await this.submitBtnClick(documentItem, {
      skipValidate: true,
    });
    if (rejectDocument) {
      this.apiActions.openDocumentEditor(this.entityType, documentItem._id, {
        forceReload: true,
      });
    } else {
      setFormFieldValue(
        this.form,
        this.formFieldPath.status,
        get(this.document, this.formFieldPath.status, ''),
        false,
      );
    }
  }

  public async cancelOrderButtonClickHandler(
    documentItem: IOrderInventory = this.document,
  ): Promise<void> {
    const message = this.i18nService.instant(
      I18n.ConfirmMessages.CancelEntity,
      {
        entityType: this.i18nService.instant(
          getSingularEntityName(ENTITY_TYPES.ORDER),
        ),
      },
    );

    const modalOptions: IModalDialogOptions = {
      title: I18n.Common.Confirm,
      text: message,
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) {
      return;
    }
    await this.doAcknowledge();
    try {
      this.uiActions.enableLoader();
      await this.ordersService
        .updateEntity(
          { [this.formFieldPath.status]: ORDER_STATUS_VALUE.Canceled },
          { endpoint: documentItem._id },
        )
        .toPromise();
      this.form.markAsPristine();
      this.apiActions.openDocumentEditor(this.entityType, documentItem._id, {
        forceReload: true,
      });
    } catch (e) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: TOASTER_TRANSACTION_FAILED_MESSAGE,
      });

      return;
    } finally {
      this.uiActions.disableLoader();
    }
  }

  public async saveButtonClickHandler(
    documentItem: IOrderInventory = this.document,
    customRedirect: CustomRedirect = '',
  ): Promise<void> {
    if (!this.form?.dirty) return;
    this.markFieldsAsDirty(['status']);
    await this.doAcknowledge();
    const saveDocument = await this.submitBtnClick(documentItem);
    // The document is `undefined` in case of Error, that is, we need to stay on the Add/Edit page
    if (!saveDocument || (!saveDocument._id && this.isNewMode())) return;

    const _id = this.getIdForRedirect(saveDocument);

    const defaultRedirect = () => {
      this.apiActions.openDocumentEditor(
        this.entityType,
        (customRedirect as string) || _id,
        {
          forceReload: true,
        },
      );
    };

    const saveDocWithRedirectId = { ...saveDocument, _id };

    this.redirectAfterSave(this.entityType, saveDocWithRedirectId, () => {
      const redirectFunc = isFunction(customRedirect)
        ? customRedirect
        : defaultRedirect;

      redirectFunc(saveDocWithRedirectId);
    });
  }

  public async saveAndSubmitButtonClickHandler(
    documentItem: IOrderInventory = this.document,
    customRedirect?: CustomRedirect,
  ): Promise<void> {
    let saveDocument = documentItem;
    const modalOptions: IModalDialogOptions = {
      title: MODAL_TEMPLATES.MODAL_TEMPLATE_CONFIRM.title,
      text: this.i18nService.instant(
        I18n.ConfirmMessages.CanNotChangeAfterSubmitted,
        {
          entityType: this.i18nService.instant(
            getSingularEntityName(this.entityType),
          ),
        },
      ),
      buttons: [
        {
          ...BTN_YES,
          class: BTN_PRIMARY_CLASS,
        },
        BTN_CANCEL,
      ],
    };
    const result = await this.uiActions.promptFormModal(modalOptions);
    if (!result.confirmed) {
      return;
    }
    await this.doAcknowledge();
    if (this.form.dirty) {
      getFormControl(this.form, this.formFieldPath.status).markAsDirty();
      saveDocument = await this.submitBtnClick(documentItem);
      // The document is `undefined` in case of Error, that is, we need to stay on the Add/Edit page
      if (!saveDocument || (!saveDocument._id && this.isNewMode())) return;
    }
    // When the order is created, the direct order status changes from "New" to "Pending",
    // there will be problems when Mapper processes the data,
    // so here save document and change status are executed separately.
    try {
      await this.ordersService
        .updateEntity(
          { [this.formFieldPath.status]: ORDER_STATUS_VALUE.Pending },
          { endpoint: saveDocument._id },
        )
        .toPromise();
    } catch (e) {
      this.uiActions.showToasterMessage({
        type: NotificationType.ERROR,
        title: TOASTER_ERROR_MESSAGE.title,
        body: TOASTER_TRANSACTION_FAILED_MESSAGE,
      });

      return;
    }
    this.uiActions.showToasterMessage({
      type: NotificationType.SUCCESS,
      title: TOASTER_SUCCESS_MESSAGE.title,
      body: TOASTER_SUBMITTED_SUCCESSFULLY_MESSAGE,
    });
    const _id = this.getIdForRedirect(saveDocument);
    const defaultRedirect = () => {
      this.apiActions.openDocumentEditor(
        this.entityType,
        (customRedirect as string) || _id,
        {
          forceReload: true,
        },
      );
    };

    const savedDocWithRedirectId = { ...saveDocument, _id };

    this.redirectAfterSave(this.entityType, savedDocWithRedirectId, () => {
      const redirectFunc = isFunction(customRedirect)
        ? customRedirect
        : defaultRedirect;

      redirectFunc(savedDocWithRedirectId);
    });
  }

  @HostListener('window:keydown', ['$event'])
  public onClick($event: KeyboardEvent): void {
    this.entityFormService.scanningService.keyTimes.push(Date.now());
    if (
      includes([KEY_CODES.TAB, KEY_CODES.ENTER], $event.key) &&
      judgeScanned(this.entityFormService.scanningService.keyTimes)
    ) {
      $event.preventDefault();
    }
  }

  public ngOnInit(): void {
    this.formRules = this.entityFormService.createFormRules(this);
    super.ngOnInit();

    // update date limit with time zone
    this.subscriptions.push(
      this.companyUtilsService
        .getCurrentCompanyTimeZoneObs()
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .subscribe((timeZone) => {
          // we think current timeZone is user company timeZone,
          // we will use UTC date to limit the minimum time,
          // because we will use user selected time as company time zone day() when save the order
          const minimumDateLimit: string = getSubtractDateString(
            toDate().toISOString(),
            ONLY_DATE_SUBSTRING_LENGTH,
          );

          this.entityFormService.minimumDate = minimumDateLimit;
          this.orderDateLimit.minimum = minimumDateLimit;
          this.deliveryDateLimit.minimum = minimumDateLimit;
          this.orderDateLimit.minimumObs.next(minimumDateLimit);
          this.deliveryDateLimit.minimumObs.next(minimumDateLimit);
        }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub?.unsubscribe());
    this.subscriptions = [];
    super.ngOnDestroy();
  }

  /**
   * will fire this when form ready
   *
   * @param form form target
   */
  public async onFormReady(form: FormGroup): Promise<void> {
    super.onFormReady(form, this.entityIdPath);
    this.defaultItemListColumn$.next(
      this.entityFormService.getDefaultOrderItemListColumn(
        this.formFieldPath,
        getFormFieldValue(this.form, 'status'),
        this,
      ),
    );

    this.currentVendorId = getFormFieldValue(
      this.form,
      this.formFieldPath.vendorEntityId,
    );
    this.currentSiteId = getFormFieldValue(
      this.form,
      this.formFieldPath.siteId,
    );

    this.setInfoMessage();

    this.subscriptions.push(this.getTopButtonBuildSubscription());

    // should add addition columns to total form when pending, completed, rejected
    this.getTransactionStatusFromEditor(
      [
        ORDER_STATUS_VALUE.Pending,
        ORDER_STATUS_VALUE.Completed,
        ORDER_STATUS_VALUE.Rejected,
      ],
      this.isAddAdditionalTotalFormColumns$,
    );
    this.getTransactionStatusFromEditor(
      [ORDER_STATUS_VALUE.New, ORDER_STATUS_VALUE.Open],
      this.isOpenStatus$,
    );
    // lock vendor when status is not in new and open.
    this.getTransactionStatusFromEditor(hiddenVendorStatus, this.hiddenVendor$);

    this.getDatePickerStatusFromEditor();

    this.subscriptions.push(
      this.getUpdateDateLimitSubs(
        this.formFieldPath.orderDate,
        this.deliveryDateLimit,
        this.orderDateLimit,
      ),
    );

    this.subscriptions.push(
      this.getUpdateDateLimitSubs(
        this.formFieldPath.deliveryDate,
        this.orderDateLimit,
        this.deliveryDateLimit,
        'maximum',
      ),
    );

    this.initIsAcceptForOrderSubscription();

    this.initVendorAndSiteSubscription();

    this.initListTotalsSubscription();

    this.initOrderSummarySubscription();

    this.createInventoryItemsMap();
  }

  public initListTotalsSubscription(): void {
    this.subscriptions.push(
      this.totalFromService.getHandleListTotalsDataSubs(
        this.form,
        this.formFieldPath,
      ),
    );
  }

  public initIsAcceptForOrderSubscription(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.form.statusChanges,
        this.fieldValueChanges(this.formFieldPath.inventoryItemVenues),
      ]).subscribe(([formStatus]) => {
        const flattenInventoryItemVenues = flatTreeDataByHasChildren(
          getFormFieldValue(this.form, this.formFieldPath.inventoryItemVenues),
        );
        const isValidForm: boolean = eq(formStatus, 'VALID');

        this.isAcceptForOrder$.next(
          // at least one quantity_received should more than the 0 and received_unit_cost can be equal to 0
          !some(
            flattenInventoryItemVenues,
            (inventoryItemVenue: IGridFlatInventoryItemVenue) =>
              !!Number(inventoryItemVenue[this.formFieldPath.itemCount]) &&
              !isNil(inventoryItemVenue[this.formFieldPath.itemCost]),
          ) ||
            !this.canManage ||
            !isValidForm,
        );
      }),
    );
  }

  public initVendorAndSiteSubscription(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.vendorEntityIdValueChange$,
        this.siteIdValueChange$,
      ]).subscribe(([vendorEntityId, siteId]) => {
        const vendorNameFormControl = this.getFormFieldControl(
          this.formFieldPath.vendorName,
        );

        if (
          !isEqual(this.currentSiteId, siteId) ||
          !isEqual(this.currentVendorId, vendorEntityId)
        ) {
          this.currentSiteId = siteId;
          this.currentVendorId = vendorEntityId;
          setFormFieldValue(
            this.form,
            this.formFieldPath.inventoryItemVenues,
            [],
          );
          setFormFieldValue(
            this.form,
            this.formFieldPath.virtualInventoryItemVenues,
            [],
          );
          this.hiddenModifiedInfo$.next(true);
          this.hiddenRemoveInfo$.next(true);
        }

        if (this.entityFormService.isOpenStatus(this.form)) {
          updateTextWithSelectedValue(
            this.entityFormService.vendorDataSource,
            vendorEntityId,
            vendorNameFormControl,
            false,
          );
        }

        this.getItemList(vendorEntityId, siteId);
      }),
    );
  }

  public initOrderSummarySubscription(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.statusValueChange$,
        this.totalFromService.totalItemData$,
        this.deliveryFeeValueChange$,
        this.taxFeeValueChange$,
        this.fieldValueChanges(this.formFieldPath.inventoryItemVenues),
      ]).subscribe(([status, totalItemData]) => {
        this.initOrderSummaryValues(
          status as ORDER_STATUS_VALUE,
          totalItemData,
        );
      }),
    );
  }

  public getSortedTreeDataByConfig(
    data: ITreeData[],
    config: ITreeConverterConfig,
  ): ITreeData[] {
    // The path used for sorting and grouping
    // The groupName used for group naming
    const { path, groupName } = config;
    const sortData = sortBy(
      map(data, (item) =>
        Object.assign(
          {},
          item,
          {
            internal_grid_row_id: `local${generateId()}`,
            ...config.additional,
          },
          config.additionalFn ? config.additionalFn(item) : {},
        ),
      ),
      (item) => toLower(get(item, path, '')),
    );

    if (!groupName) return sortData;

    return map(toPairs(groupBy(sortData, path)), ([name, children]) => {
      const childrenConfig = config.childrenConfig;
      const newChildren = this.getSortedTreeDataByConfig(
        children,
        childrenConfig,
      );

      return {
        [groupName]: name,
        children: newChildren,
        internal_grid_row_id: `local${generateId()}`,
        error: some(newChildren, { error: true }),
        ...config.additionalTreeNode,
        ...(config.additionalTreeFn ? config.additionalTreeFn(name) : {}),
      };
    });
  }

  public createInventoryItemsMap(): void {
    // get required field in saved order.
    const savedInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryItemVenues,
    );
    this.inventoryItemVenueMap = reduce(
      savedInventoryItemVenues,
      (itemMap, inventoryItemVenue) => {
        return {
          ...itemMap,
          [inventoryItemVenue.item_entity_id]: {
            _id: get(inventoryItemVenue, '_id', generateObjectId()),
            majorCategory: get(inventoryItemVenue, 'major_category_name'),
            minorCategory: get(inventoryItemVenue, 'minor_category_name'),
            [this.formFieldPath.error]: get(
              inventoryItemVenue,
              this.formFieldPath.error,
              false,
            ),
            [this.formFieldPath.itemCost]: get(
              inventoryItemVenue,
              this.formFieldPath.itemCost,
            ),
            [this.formFieldPath.itemCount]: get(
              inventoryItemVenue,
              this.formFieldPath.itemCount,
            ),
            [this.formFieldPath.initialItemCost]: get(
              inventoryItemVenue,
              this.formFieldPath.initialItemCost,
            ),
            [this.formFieldPath.initialItemCount]: get(
              inventoryItemVenue,
              this.formFieldPath.initialItemCount,
            ),
          },
        };
      },
      {},
    );
  }

  public processInventoryItemVenuesTreeData(
    item: IInventoryItemVenue,
  ): IInventoryItemVenue {
    const selectedVendorEntityId = getFormFieldValue(
      this.form,
      this.formFieldPath.vendorEntityId,
    );
    const selectedMeasure: IVendorMeasure = getMeasureFromVendor(
      item.vendors,
      selectedVendorEntityId,
      get(item, this.formFieldPath.measureEntityId),
    );

    // Mix DM and XVI inventory item data
    const mixedData = {
      ...item,
      error: get(this.inventoryItemVenueMap, [item.entity_id, 'error'], false),
      majorCategory: get(
        this.inventoryItemVenueMap,
        [item.entity_id, 'majorCategory'],
        get(item, COMMON_FORM_PATH.reportingCategoryMajorName, ''),
      ),
      minorCategory: get(
        this.inventoryItemVenueMap,
        [item.entity_id, 'minorCategory'],
        get(item, COMMON_FORM_PATH.reportingCategoryMinorName, ''),
      ),
      [this.formFieldPath.itemEntityId]: get(item, 'entity_id'),
      [this.formFieldPath.itemName]: get(item, 'name'),
      [this.formFieldPath.itemCost]: get(
        this.inventoryItemVenueMap,
        [item.entity_id, this.formFieldPath.itemCost],
        get(selectedMeasure, 'vendor_cost', 0),
      ),
      [this.formFieldPath.itemCount]: get(
        this.inventoryItemVenueMap,
        [item.entity_id, this.formFieldPath.itemCount],
        null,
      ),
      [this.formFieldPath.initialItemCost]: get(
        this.inventoryItemVenueMap,
        [item.entity_id, this.formFieldPath.initialItemCost],
        0,
      ),
      [this.formFieldPath.initialItemCount]: get(
        this.inventoryItemVenueMap,
        [item.entity_id, this.formFieldPath.initialItemCount],
        null,
      ),
      [this.formFieldPath.measureEntityId]: get(
        item,
        this.formFieldPath.measureEntityId,
        '',
      ),
      [this.formFieldPath.glAccountEntityId]: get(
        item,
        'gl_account.entity_id',
        '',
      ),
      [this.formFieldPath.glAccountName]: get(item, 'gl_account.name', ''),
    };

    return appendInitialFactorValueAndCount(mixedData);
  }

  public getInventoryItemVenuesTreeData(
    items: IInventoryItemVenue[],
  ): ITreeData[] {
    return this.getSortedTreeDataByConfig(items, {
      path: COMMON_FORM_PATH.reportingCategoryMajorName,
      groupName: this.formFieldPath.itemName,
      additionalTreeFn: (name): IBaseEntityType => {
        return {
          isMajorCategoryRow: true,
          reporting_category_major: {
            name,
          },
        };
      },
      childrenConfig: {
        path: COMMON_FORM_PATH.reportingCategoryMinorName,
        groupName: this.formFieldPath.itemName,
        additionalTreeFn: (name): IBaseEntityType => {
          return {
            isMinorCategoryRow: true,
            reporting_category_minor: {
              name,
            },
          };
        },
        childrenConfig: {
          path: 'name',
          additional: {
            isLeaf: true,
          },
          additionalFn: this.processInventoryItemVenuesTreeData.bind(this),
        },
      },
    });
  }

  public getTreeDataBySavedInventoryItemVenues(
    items: IGridFlatInventoryItemVenue[],
    itemName: string = 'name',
  ): ITreeData {
    return this.getSortedTreeDataByConfig(items, {
      path: 'major_category_name',
      groupName: this.formFieldPath.itemName,
      additionalTreeFn: (name): IBaseEntityType => {
        return {
          isMajorCategoryRow: true,
          reporting_category_major: {
            name,
          },
        };
      },
      childrenConfig: {
        path: 'minor_category_name',
        groupName: this.formFieldPath.itemName,
        additionalTreeFn: (name): IBaseEntityType => {
          return {
            isMinorCategoryRow: true,
            reporting_category_minor: {
              name,
            },
          };
        },
        childrenConfig: {
          path: itemName,
          additionalFn(item: any): any {
            return {
              ...item,
              isLeaf: true,
              reporting_category_major: {
                name: item?.major_category_name,
              },
              reporting_category_minor: {
                name: item?.minor_category_name,
              },
            };
          },
        },
      },
    });
  }

  /**
   * Initialize the total value according to different status
   */
  public initOrderSummaryValues(
    status: ORDER_STATUS_VALUE,
    totalItemData: IGridFlatInventoryItemVenueTotal,
  ): void {
    const isOpenStatus = includes(
      [
        ORDER_STATUS_VALUE.New,
        ORDER_STATUS_VALUE.Open,
        ORDER_STATUS_VALUE.Canceled,
      ],
      status,
    );
    const initValue = 0.0;
    const totalCost = totalItemData?.received_total_cost || initValue;
    const deliveryFee =
      getFormFieldValue(this.form, this.formFieldPath.deliveryFee) || initValue;
    const taxFee =
      getFormFieldValue(this.form, this.formFieldPath.taxFee) || initValue;
    const formatTotalCostShow = this.numberFormatService.getNumberFormatter(
      true,
      this.getNegativeNumberType,
      {
        defaultFormatValue: '0',
      },
    )({ value: totalCost });
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTotalCostShow,
      formatTotalCostShow,
      false,
    );
    const grandTotal = new DecimalNumber(totalCost)
      .add(deliveryFee)
      .add(taxFee);
    const formatGrandTotal = this.numberFormatService.getNumberFormatter(
      true,
      this.getNegativeNumberType,
      {
        defaultFormatValue: '0',
      },
    )({ value: isOpenStatus ? initValue : grandTotal });
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualGrandTotal,
      formatGrandTotal,
      false,
    );
  }

  public getNegativeNumberType(): NegativeNumberType {
    return NegativeNumberType.Common;
  }

  /**
   * check is inventory item empty
   *
   * @returns inventory item empty
   */
  public checkIsGridEmpty(): boolean {
    return isEmpty(
      this.getFormFieldValue(this.formFieldPath.inventoryItemVenues),
    );
  }

  /**
   * check is top section valid
   *
   * @returns is valid
   */
  public checkIsTopSectionValid(): boolean {
    return (
      this.getFormFieldControl(this.formFieldPath.vendorEntityId).valid &&
      this.getFormFieldControl(this.formFieldPath.siteId).valid &&
      this.getFormFieldControl(this.formFieldPath.orderDate).valid &&
      this.getFormFieldControl(this.formFieldPath.deliveryDate).valid
    );
  }

  /**
   * build page top buttons with form status
   *
   * @returns form status change subscription
   */
  public getTopButtonBuildSubscription(): Subscription {
    return observableCombineLatest([
      this.getFieldChangeFilterEmpty(this.formFieldPath.status),
      this.totalFromService.totalItemData$,
      this.isInventoryItemVenuesGridBusy$,
    ]).subscribe(
      ([status, totalItemData, isInventoryItemVenuesGridBusy]: [
        ORDER_STATUS_VALUE,
        IGridFlatInventoryItemVenueTotal,
        boolean,
      ]) => {
        const isOpenStatus = [
          ORDER_STATUS_VALUE.New,
          ORDER_STATUS_VALUE.Open,
        ].includes(status);
        const isPendingStatus = status === ORDER_STATUS_VALUE.Pending;
        const isCanceledStatus = status === ORDER_STATUS_VALUE.Canceled;
        this.hiddenSummary$.next(isOpenStatus || isCanceledStatus);
        this.disabledChildButtonDropdown = isPendingStatus;
        this.summaryDisabled = !isPendingStatus;
        this.initOrderSummaryValues(status, totalItemData);
        this.pageTopButtons$.next([
          {
            caption: I18n.Common.Accept,
            class: 'btn btn-primary mr-10',
            click: this.acceptButtonClickHandler.bind(this),
            disabledObs: this.isAcceptForOrder$,
            hidden: !isPendingStatus,
          },
          {
            caption: I18n.Common.Reject,
            class: 'btn btn-danger mr-10',
            click: this.rejectAllButtonClickHandler.bind(this),
            disabled: (): boolean => !this.canManage,
            hidden: (): boolean =>
              !get(this.document, '_id') || !isPendingStatus,
          },
          {
            ...this.topSaveButtonDefinition,
            disabled: (): boolean =>
              this.getSaveDisabledDefaultCondition() ||
              isInventoryItemVenuesGridBusy,
          },
        ]);

        this.pageTopDropdownButtons$.next([
          {
            caption: I18n.Common.Submit,
            value: 'edit-xux-btn-save-and-submit',
            click: this.saveAndSubmitButtonClickHandler.bind(this),
            hidden: (): boolean =>
              !this.form?.valid ||
              !this.canEdit ||
              !this.canManage ||
              !isOpenStatus ||
              isInventoryItemVenuesGridBusy,
          },
          {
            ...this.saveAndNewButtonDefinition,
            hidden: (): boolean => {
              return (
                !(isOpenStatus || isPendingStatus) ||
                isInventoryItemVenuesGridBusy ||
                getFormBtnHidden(this.saveAndNewButtonDefinition.hidden)
              );
            },
          },
          {
            ...this.submitAndNewButtonDefinition,
            hidden: (): boolean => {
              return (
                !isOpenStatus ||
                isInventoryItemVenuesGridBusy ||
                getFormBtnHidden(this.submitAndNewButtonDefinition.hidden)
              );
            },
          },
          {
            ...this.discardButtonDefinition,
            hidden: (): boolean =>
              !(isOpenStatus || isPendingStatus) ||
              getFormBtnHidden(this.discardButtonDefinition.hidden),
          },
          {
            caption: I18n.Forms.OrderForm.CancelInventoryOrder,
            value: 'edit-xux-btn-cancel-order',
            click: this.cancelOrderButtonClickHandler.bind(this),
            hidden: (): boolean =>
              !get(this.document, '_id') || !isOpenStatus || !this.canManage,
          },
        ]);
      },
    );
  }

  public async saveAndNewButtonClickHandler(
    documentItem: IOrderInventory = this.document,
  ): Promise<void> {
    await this.saveButtonClickHandler(
      documentItem,
      this.getCreateWithDataRedirect().bind(this),
    );
  }

  public async submitAndNewButtonClickHandler(
    documentItem: IOrderInventory = this.document,
  ): Promise<void> {
    await this.saveAndSubmitButtonClickHandler(
      documentItem,
      this.getCreateWithDataRedirect().bind(this),
    );
  }

  /**
   * update target limit by alternativeLimit or path control data
   *
   * @param path control path, will subscribe control value change through this path
   * @param targetLimit target limit
   * @param alternativeLimit alternative limit, if control data is empty, will use this data to replace
   * @param type update limit type
   * @returns control value changes subscription
   */
  public getUpdateDateLimitSubs<T>(
    path: string,
    targetLimit: ILimit<T>,
    alternativeLimit: ILimit<T>,
    type: keyof ILimit<T> = 'minimum',
  ): Subscription {
    return this.fieldValueChanges(path)
      .pipe(
        rx.filter(() => this.getFormFieldControl(path)?.valid), // only valid data need to be send
        rx.distinctUntilChanged(),
      )
      .subscribe((data) => {
        targetLimit[type] = isEmpty(data) ? alternativeLimit[type] : data;
        targetLimit[`${type}Obs`].next(targetLimit[type]);
      });
  }

  /**
   * get vendor item by vendor entity id and site id
   *
   * @param vendorEntityId target vendor entity id
   * @param siteId target site id
   */
  public async getItemList(
    vendorEntityId: string,
    siteId: string,
  ): Promise<void> {
    const isOpenStatus = [
      ORDER_STATUS_VALUE.New,
      ORDER_STATUS_VALUE.Open,
    ].includes(getFormFieldValue(this.form, this.formFieldPath.status));
    this.isInventoryItemVenuesGridBusy$.next(true);
    const inventoryItemVenueListService = this.entityServiceProvider.getService(
      ENTITY_TYPES.INVENTORY_ITEM_VENUE,
    );
    if (isOpenStatus) {
      try {
        const data = (await inventoryItemVenueListService
          .fetchCurrentList(
            {
              filter: `vendors/entity_id eq in(${vendorEntityId}) and measures/transactions/delivery eq in(true)`,
            },
            [siteId],
          )
          .pipe(
            rx.map(({ items }: IBaseEntityResponse): IBaseEntityType[] => {
              return map(items, (inventoryItem) => {
                const virtualInventoryItems = getFormFieldValue(
                  this.form,
                  this.formFieldPath.virtualInventoryItemVenues,
                );
                const measure = find(
                  virtualInventoryItems,
                  (item) => item.item_entity_id === inventoryItem?.entity_id,
                );
                const orderingMeasures =
                  this.getOrderingMeasures(inventoryItem);
                // sort the ordering Unit by description
                const sortMeasures = sortBy(
                  map(orderingMeasures, (filterItem) => {
                    return {
                      ...filterItem,
                      text: generateUnitTextV2(filterItem, {
                        numberFormatService: this.numberFormatService,
                        measureTransactionType: MEASURE_TRANSACTION.Order,
                      }),
                      value: filterItem.entity_id,
                      description: filterItem.description,
                    };
                  }),
                  (item) => toLower(item.description),
                );
                set(inventoryItem, 'measures', sortMeasures);
                // if the document have the measureEntityId use it else use first value
                const measureEntityId = get(
                  measure,
                  this.formFieldPath.measureEntityId,
                  null,
                );
                inventoryItem[this.formFieldPath.measureEntityId] =
                  measureEntityId ||
                  get(
                    getDefaultMeasure(sortMeasures, MEASURE_TRANSACTION.Order),
                    ENTITY_ID_PATH,
                  );

                return inventoryItem;
              });
            }),
          )
          .toPromise()) as IInventoryItemVenue[];
        const inventoryItemVenuesTreeData =
          this.getInventoryItemVenuesTreeData(data);
        this.entityFormService.updateItemsByCompanyPreferences(
          inventoryItemVenuesTreeData,
          this.formFieldPath.inventoryItemVenues,
          isEmpty(inventoryItemVenuesTreeData),
        );
        if (isEmpty(inventoryItemVenuesTreeData)) {
          getFormControl(
            this.form,
            this.formFieldPath.inventoryItemVenues,
          ).markAsPristine();
        }
        this.inventoryItemVenueMap = {};
      } catch (e) {
        this.uiActions.showToasterMessage({
          type: NotificationType.ERROR,
          title: e.title,
          body: e.message,
        });
        this.isInventoryItemVenuesGridBusy$.next(false);
      }
    } else {
      const inventoryItemVenues = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryItemVenues,
      );

      const inventoryItemIds = map(inventoryItemVenues, 'item_entity_id');
      const { items } = await inventoryItemVenueListService
        .fetchList(
          {
            filter: gt(size(inventoryItemIds), MAX_URL_QUERY_NUMBER)
              ? 'is_active eq true'
              : `entity_id eq in(${inventoryItemIds.join()})`,
            select: [ENTITY_ID_PATH, 'upc_codes'],
          },
          { fetchFullListData: true },
        )
        .toPromise();

      const itemsMap = keyBy(items, ENTITY_ID_PATH);
      forEach(inventoryItemVenues, (item) => {
        const itemInDM = get(itemsMap, item.item_entity_id);
        const upcCodes = get(itemInDM, 'upc_codes', []);
        set(item, 'upc_codes', upcCodes);
      });

      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryItemVenues,
        inventoryItemVenues,
        false,
      );

      /**
       * when the order is not open status, use the item_name to sort the inventory_items,
       * it will not change the submit data to API
       */
      const treeData = this.getTreeDataBySavedInventoryItemVenues(
        inventoryItemVenues,
        this.formFieldPath.itemName,
      );
      this.entityFormService.updateItemsByCompanyPreferences(
        treeData,
        this.formFieldPath.inventoryItemVenues,
        false,
      );
    }
    this.isInventoryItemVenuesGridBusy$.next(false);
    setTimeout(() => {
      this.entityFormService.listGridApi.expandAll();
    });
    getFormControl(
      this.form,
      this.formFieldPath.inventoryItemVenues,
    )?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });
  }

  public setInfoMessage(): void {
    const updatedItems = get(this.document, 'updated_items');
    const unavailableItems = get(this.document, 'unavailable_items');
    if (!isEmpty(updatedItems)) {
      this.hiddenModifiedInfo$.next(false);
      setTimeout(() => {
        this.modifiedInfoTitle$.next(
          this.i18nService.instant(I18n.InfoMessages.ItemListModified, {
            count: values(updatedItems).length,
            entityType: this.i18nService.instant(
              getSingularEntityName(ENTITY_TYPES.ORDER),
            ),
          }),
        );
      });
    }
    if (!isEmpty(unavailableItems)) {
      const noLongerAvailableItems: string[] = map(
        values(unavailableItems),
        (item) => {
          return `${item.item_name} - ${item.measure_description}`;
        },
      );
      this.hiddenRemoveInfo$.next(false);
      setTimeout(() => {
        this.removeInfoTitle$.next(
          this.i18nService.instant(
            I18n.InfoMessages.OrderItemsNoLongerAvailable,
            {
              items: noLongerAvailableItems.join(', '),
            },
          ),
        );
      });
    }
  }

  public handleExternalOrderNumber(): boolean {
    const showStatus = [
      ORDER_STATUS_VALUE.New,
      ORDER_STATUS_VALUE.Open,
      ORDER_STATUS_VALUE.Pending,
    ];

    return !showStatus.includes(this.form.value.status);
  }

  public async printPdfClickHandler(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    params: Record<string, string>,
  ): Promise<void> {
    if (!this.canPrint) {
      return;
    }

    await this.orderPdfService.handlePdfClick(this, 'print');
  }

  public async exportPdfClickHandler(): Promise<void> {
    if (!this.canExportPDF) {
      return;
    }

    await this.orderPdfService.handlePdfClick(this, 'export');
  }

  public async openOrderAddPendingItemsPage(): Promise<void> {
    if (this.form.dirty) {
      const result = await this.uiActions.promptFormModal(
        addPendingOrderModalOptions({
          i18nService: this.i18nService,
        }),
      );
      if (!result.confirmed) return;

      if (get(result, 'value.needToSave')) {
        await this.saveButtonClickHandler(
          this.document,
          `add-pending/${get(this, 'document._id')}`,
        );

        return;
      } else {
        this.form.markAsPristine();
      }
    }

    this.apiActions.openDocumentEditor(
      this.entityType,
      `add-pending/${get(this, 'document._id')}`,
      {
        forceReload: true,
      },
    );
  }

  private getTransactionStatusFromEditor(
    orderStatus: ORDER_STATUS_VALUE[],
    obs: BehaviorSubject<boolean>,
  ): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.status).subscribe((status) => {
        obs.next(includes(orderStatus, status));
      }),
    );
  }

  private getDatePickerStatusFromEditor(): void {
    this.subscriptions.push(
      this.isOpenStatus$.subscribe((isOpenStatus: boolean) => {
        this.datePickerDisabled$.next(!isOpenStatus);
      }),
    );
  }

  private getOrderingMeasures(inventoryItem: IBaseEntityType): IMeasure[] {
    return get(
      find(inventoryItem.vendors, (vendor) =>
        isEqual(
          vendor.entity_id,
          this.getFormFieldValue(this.formFieldPath.vendorEntityId),
        ),
      ),
      'measures',
    );
  }

  // eslint-disable-next-line max-lines
}
